/**
 * /dashboard/auditoria
 *
 * Rastreabilidade — lista todos os eventos de auditoria com filtros,
 * detalhe em modal (diff antes/depois) e export CSV.
 *
 * Acesso restrito a admin/master (validado tambem no backend).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  useToast,
} from "@payflow/ui";

const API_BASE = (import.meta as any).env?.VITE_API_URL || "http://localhost:8787";
const TOKEN_KEY = "payflow_token";

interface AuditLog {
  id: string;
  tenantId: string | null;
  usuarioId: string | null;
  usuarioNome: string | null;
  usuarioEmail: string | null;
  acao: string;
  entidade: string;
  entidadeId: string | null;
  descricao: string | null;
  dadosAnteriores: Record<string, unknown> | null;
  dadosNovos: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  requestId: string | null;
  createdAt: string;
}

async function apiFetch(path: string, options: RequestInit = {}) {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error(json.message || `HTTP ${res.status}`);
  }
  return json;
}

const ENTIDADES = [
  "", "emprestimo", "parcela", "cliente", "contrato", "pagamento",
  "usuario", "perfil_acesso", "tenant", "tenant_loan_config", "tenant_integrations",
  "pix_charge", "pix_webhook", "loan_request", "client_credential", "whatsapp_message",
  "onboarding_flows",
];

const ACOES = [
  "", "criar", "atualizar", "excluir", "restaurar", "login", "logout", "login_falha",
  "alterar_senha", "alterar_permissao", "registrar_pagamento", "estornar_pagamento",
  "pix_enviado", "pix_recebido", "contrato_gerado", "contrato_enviado", "contrato_assinado",
  "contrato_cancelado", "whatsapp_send", "exportar",
];

function formatDateTime(value: string): string {
  try {
    return new Date(value).toLocaleString("pt-BR");
  } catch {
    return value;
  }
}

function diffFields(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null
): string[] {
  if (!before && !after) return [];
  if (!before) return Object.keys(after || {});
  if (!after) return Object.keys(before);
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  return Array.from(keys).filter(
    (k) => JSON.stringify(before[k]) !== JSON.stringify(after[k])
  );
}

export function AuditPage() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [detail, setDetail] = useState<AuditLog | null>(null);

  // Filtros
  const [acao, setAcao] = useState("");
  const [entidade, setEntidade] = useState("");
  const [entidadeId, setEntidadeId] = useState("");
  const [usuarioId, setUsuarioId] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");

  const buildQuery = useCallback(
    (pageOverride?: number) => {
      const p = new URLSearchParams();
      p.set("page", String(pageOverride ?? page));
      p.set("limit", String(limit));
      if (acao) p.set("acao", acao);
      if (entidade) p.set("entidade", entidade);
      if (entidadeId.trim()) p.set("entidadeId", entidadeId.trim());
      if (usuarioId.trim()) p.set("usuarioId", usuarioId.trim());
      if (dataInicio) p.set("dataInicio", dataInicio);
      if (dataFim) p.set("dataFim", dataFim);
      return p.toString();
    },
    [page, limit, acao, entidade, entidadeId, usuarioId, dataInicio, dataFim]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch(`/v1/admin/audit?${buildQuery()}`);
      setLogs(data.data || []);
      setTotal(data.meta?.total || 0);
    } catch (err: any) {
      toast({ variant: "error", title: err.message || t("audit.load_error") });
    } finally {
      setLoading(false);
    }
  }, [buildQuery, toast, t]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = () => {
    setPage(1);
    load();
  };

  const clearFilters = () => {
    setAcao("");
    setEntidade("");
    setEntidadeId("");
    setUsuarioId("");
    setDataInicio("");
    setDataFim("");
    setPage(1);
  };

  const exportCsv = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const url = `${API_BASE}/v1/admin/audit/export.csv?${buildQuery()}`;
    try {
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `auditoria-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err: any) {
      toast({ variant: "error", title: err.message || t("audit.export_error") });
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t("audit.title")}</h1>
        <p className="text-muted-foreground text-sm mt-1">{t("audit.subtitle")}</p>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{t("audit.filters")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                {t("audit.acao")}
              </label>
              <Select
                value={acao}
                onChange={(e) => setAcao(e.target.value)}
                options={ACOES.map((a) => ({ value: a, label: a || t("audit.any") }))}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                {t("audit.entidade")}
              </label>
              <Select
                value={entidade}
                onChange={(e) => setEntidade(e.target.value)}
                options={ENTIDADES.map((en) => ({ value: en, label: en || t("audit.any") }))}
              />
            </div>
            <Input
              label={t("audit.entidade_id")}
              value={entidadeId}
              onChange={(e) => setEntidadeId(e.target.value)}
              placeholder="UUID"
            />
            <Input
              label={t("audit.usuario_id")}
              value={usuarioId}
              onChange={(e) => setUsuarioId(e.target.value)}
              placeholder="UUID"
            />
            <Input
              label={t("audit.data_inicio")}
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
            />
            <Input
              label={t("audit.data_fim")}
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
            />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={applyFilters}>{t("audit.apply")}</Button>
            <Button variant="outline" onClick={clearFilters}>{t("audit.clear")}</Button>
            <Button variant="outline" onClick={exportCsv}>{t("audit.export_csv")}</Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">
            {t("audit.events")}{" "}
            <span className="text-muted-foreground font-normal">({total})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t("common.loading")}
            </p>
          ) : logs.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t("audit.empty")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="pb-2 pr-3 font-medium">{t("audit.col_when")}</th>
                    <th className="pb-2 pr-3 font-medium">{t("audit.col_action")}</th>
                    <th className="pb-2 pr-3 font-medium">{t("audit.col_entity")}</th>
                    <th className="pb-2 pr-3 font-medium">{t("audit.col_user")}</th>
                    <th className="pb-2 pr-3 font-medium">{t("audit.col_desc")}</th>
                    <th className="pb-2 font-medium text-right">{t("audit.col_actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-muted/40">
                      <td className="py-2 pr-3 text-xs text-muted-foreground whitespace-nowrap">
                        {formatDateTime(log.createdAt)}
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs">
                        <span className="rounded bg-muted px-1.5 py-0.5">{log.acao}</span>
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs text-foreground">
                        {log.entidade}
                      </td>
                      <td className="py-2 pr-3 text-xs">
                        <div className="text-foreground">{log.usuarioNome || "-"}</div>
                        <div className="text-muted-foreground">{log.usuarioEmail || ""}</div>
                      </td>
                      <td className="py-2 pr-3 text-xs text-foreground max-w-md truncate">
                        {log.descricao || "-"}
                      </td>
                      <td className="py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDetail(log)}
                        >
                          {t("audit.view")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginacao */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
              <span>
                {t("audit.page")} {page} / {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || loading}
                  onClick={() => { setPage((p) => Math.max(1, p - 1)); }}
                >
                  {t("audit.prev")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages || loading}
                  onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); }}
                >
                  {t("audit.next")}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de detalhe */}
      {detail && <AuditDetailModal log={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function AuditDetailModal({ log, onClose }: { log: AuditLog; onClose: () => void }) {
  const { t } = useTranslation();
  const changedFields = useMemo(
    () => diffFields(log.dadosAnteriores, log.dadosNovos),
    [log]
  );

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("audit.detail_title")}</DialogTitle>
          <p className="text-xs text-muted-foreground mt-1 font-mono">
            {log.id}
          </p>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("audit.col_when")} value={formatDateTime(log.createdAt)} />
            <Field label={t("audit.col_action")} value={log.acao} mono />
            <Field label={t("audit.col_entity")} value={log.entidade} mono />
            <Field label={t("audit.entidade_id")} value={log.entidadeId || "-"} mono />
            <Field label={t("audit.col_user")} value={log.usuarioNome || "-"} />
            <Field label="Email" value={log.usuarioEmail || "-"} />
            <Field label="IP" value={log.ipAddress || "-"} mono />
            <Field label="Request ID" value={log.requestId || "-"} mono />
          </div>

          {log.descricao && (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">
                {t("audit.col_desc")}
              </div>
              <div className="rounded-lg border border-border bg-muted/30 p-3 text-foreground">
                {log.descricao}
              </div>
            </div>
          )}

          {changedFields.length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-2">
                {t("audit.changes")} ({changedFields.length})
              </div>
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="min-w-full text-xs">
                  <thead className="bg-muted/60 text-muted-foreground">
                    <tr>
                      <th className="p-2 text-left">{t("audit.field")}</th>
                      <th className="p-2 text-left">{t("audit.before")}</th>
                      <th className="p-2 text-left">{t("audit.after")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {changedFields.map((field) => {
                      const before = log.dadosAnteriores?.[field];
                      const after = log.dadosNovos?.[field];
                      return (
                        <tr key={field}>
                          <td className="p-2 font-mono text-foreground align-top">{field}</td>
                          <td className="p-2 font-mono text-muted-foreground align-top break-all">
                            {formatValue(before)}
                          </td>
                          <td className="p-2 font-mono text-foreground align-top break-all">
                            {formatValue(after)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {log.metadata && Object.keys(log.metadata).length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">
                {t("audit.metadata")}
              </div>
              <pre className="rounded-lg border border-border bg-muted/30 p-3 text-xs overflow-x-auto">
                {JSON.stringify(log.metadata, null, 2)}
              </pre>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className={`mt-0.5 ${mono ? "font-mono text-xs" : "text-sm"} text-foreground break-all`}>
        {value}
      </div>
    </div>
  );
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}
